import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import { articles, channels } from "@/mocks/articles";
import { chipSolid, chipSoft } from "@/utils/ui";
import { formatViews } from "@/utils/format";

const site = "https://immaple.cn";

export default function ArticlePage() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center text-center px-4 py-24">
          <p className="text-6xl font-heading font-bold text-background-300">404</p>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">这篇文章不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-500">换个瓜尝尝吧，好吃的一定还在。</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 whitespace-nowrap px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600">
            回到首页吃瓜
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const channel = channels.find((c) => c.id === article.category)!;
  const sameCat = articles.filter((a) => a.category === article.category && a.id !== article.id).sort((x, y) => y.views - x.views);
  const others = articles.filter((a) => a.category !== article.category).sort((x, y) => y.views - x.views).slice(0, 4);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.subtitle,
    image: article.image,
    datePublished: `${article.date}T08:00:00+08:00`,
    author: { "@type": "Organization", name: "在线吃瓜编辑部" },
    publisher: { "@type": "Organization", name: "在线吃瓜", url: site },
    mainEntityOfPage: `${site}/article/${article.id}`,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: site },
        { "@type": "ListItem", position: 2, name: channel.name, item: `${site}/channel/${channel.id}` },
        { "@type": "ListItem", position: 3, name: article.title, item: `${site}/article/${article.id}` },
      ],
    },
  };

  return (
    <>
      <Seo
        title={`${article.title}｜${channel.name} - 在线吃瓜`}
        description={article.subtitle}
        keywords={`在线吃瓜,${channel.name},${article.tags.join(",")}`}
        canonicalPath={`/article/${article.id}`}
        jsonLd={jsonLd}
      />
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 bg-background-50">
          {/* breadcrumb */}
          <div className="border-b border-background-200">
            <div className="mx-auto max-w-7xl px-4 md:px-6 py-4">
              <nav className="text-xs text-foreground-500" aria-label="面包屑">
                <ol className="flex items-center gap-1.5 flex-wrap">
                  <li><Link to="/" className="hover:text-primary-700">首页</Link></li>
                  <li className="text-foreground-300"><i className="ri-arrow-right-s-line"></i></li>
                  <li><Link to={`/channel/${channel.id}`} className="hover:text-primary-700">{channel.name}</Link></li>
                  <li className="text-foreground-300"><i className="ri-arrow-right-s-line"></i></li>
                  <li className="text-foreground-800 font-medium line-clamp-1 max-w-[18rem]">{article.title}</li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* article */}
            <article className="lg:col-span-8">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${chipSolid(channel.color)}`}>{channel.name}</span>
                {article.tags.slice(0, 2).map((t) => (
                  <span key={t} className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs ${chipSoft("secondary")}`}>{t}</span>
                ))}
              </div>
              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">{article.title}</h1>
              <p className="mt-4 text-base md:text-lg leading-8 text-foreground-600">{article.subtitle}</p>

              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500 border-y border-background-200 py-3">
                <span className="flex items-center gap-1.5 text-foreground-800"><span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary-100 text-primary-700"><i className="ri-user-3-line"></i></span>{article.author}</span>
                <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
                <span><i className="ri-time-line mr-1"></i>预计阅读 {article.readTime}</span>
                <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(article.views)} 热度</span>
              </div>

              <div className="relative mt-6 h-60 md:h-96 overflow-hidden rounded-xl">
                <img src={article.image} alt={article.title} className="absolute inset-0 w-full h-full object-cover" />
              </div>

              <div className="mt-7 space-y-7">
                {article.content.map((sec, si) => (
                  <div key={si}>
                    {sec.heading && (
                      <h2 className="mb-3 flex items-center gap-2.5 font-heading text-lg md:text-xl font-bold text-foreground-950">
                        <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
                        {sec.heading}
                      </h2>
                    )}
                    {sec.paragraphs.map((p, pi) => (
                      <p
                        key={pi}
                        className={`text-[15px] leading-8 text-foreground-700 ${si === 0 && pi === 0 && !sec.heading ? "text-base md:text-lg text-foreground-800 first-letter:float-left first-letter:mr-2 first-letter:text-4xl first-letter:font-heading first-letter:font-bold first-letter:text-primary-600" : "mt-0"} ${sec.paragraphs.length > 1 ? "mt-4" : ""}`}
                      >
                        {p}
                      </p>
                    ))}
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {article.tags.map((t) => (
                  <span key={t} className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs ${chipSoft(channel.color)}`}>
                    <i className="ri-price-tag-3-line mr-1"></i>{t}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-xl border border-background-200 bg-background-100 p-5">
                <span className="flex items-center justify-center w-11 h-11 shrink-0 rounded-lg bg-primary-500 text-background-50"><i className="ri-edit-2-line text-lg"></i></span>
                <div className="flex-1">
                  <p className="font-heading text-sm font-bold text-foreground-900">在线吃瓜编辑部</p>
                  <p className="mt-1 text-xs leading-5 text-foreground-500">关注娱乐行业机制与作品逻辑，不猎奇、不造谣。发现线索或希望补充信息？欢迎邮件联系编辑团队。</p>
                </div>
                <Link to="/about" className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800">
                  了解投稿规则<i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              {/* 同栏目上下篇 */}
              {sameCat.length > 0 && (
                <nav className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" aria-label="同栏目更多文章">
                  {sameCat.slice(0, 2).map((n) => (
                    <Link key={n.id} to={`/article/${n.id}`} className="group rounded-lg border border-background-200 bg-background-100 p-4 hover:border-primary-300 transition-colors">
                      <span className="text-xs text-foreground-500">{channel.name} · 继续读</span>
                      <p className="mt-1.5 text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">{n.title}</p>
                    </Link>
                  ))}
                </nav>
              )}
            </article>

            {/* aside */}
            <aside className="lg:col-span-4 space-y-6">
              <div className="rounded-xl border border-background-200 bg-background-100 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <span className="w-1.5 h-4 rounded-full bg-accent-500"></span>
                  同类「{channel.name}」
                </h3>
                <div className="mt-3 space-y-1">
                  {sameCat.length === 0 && <p className="text-sm text-foreground-500 py-3">这个栏目暂时只有本篇，去看看别的好瓜吧。</p>}
                  {sameCat.slice(0, 5).map((n) => (
                    <Link key={n.id} to={`/article/${n.id}`} className="group flex gap-3 items-start py-2.5 border-b border-background-200/70 last:border-0">
                      <span className="w-10 h-10 shrink-0 overflow-hidden rounded-md">
                        <img src={n.image} alt="" className="w-full h-full object-cover" />
                      </span>
                      <span className="text-sm font-medium text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">{n.title}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <span className="w-1.5 h-4 rounded-full bg-primary-500"></span>
                  大家都在吃
                </h3>
                <ol className="mt-3 space-y-1">
                  {others.map((n, i) => (
                    <li key={n.id}>
                      <Link to={`/article/${n.id}`} className="group flex items-center gap-3 py-2.5 border-b border-background-200/70 last:border-0">
                        <span className="font-heading text-sm font-bold text-primary-300 w-4 text-center">{i + 1}</span>
                        <span className="text-sm font-medium text-foreground-800 leading-snug line-clamp-1 group-hover:text-primary-700 transition-colors">{n.title}</span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <Link to={`/channel/${channel.id}`} className="group block rounded-xl overflow-hidden border border-accent-200 bg-accent-100 p-5">
                <p className="text-xs text-accent-800 tracking-widest">还在意犹未尽？</p>
                <p className="mt-2 font-heading text-lg font-bold text-foreground-950">去「{channel.name}」栏目继续深挖</p>
                <span className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-accent-900">
                  去看看<i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </span>
              </Link>
            </aside>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    </>
  );
}